export function hasOutboundCallMetadata(call) {
    return Boolean(call?.source === "outbound_playbook_task" ||
        call?.source === "outbound_sip" ||
        call?.outboundPlaybookRunId ||
        call?.outboundTaskId ||
        call?.outboundTaskAttemptId ||
        call?.outboundPlaybookId);
}
export function payloadEntries(payload, schema) {
    if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
        return [];
    }
    const properties = schema?.properties &&
        typeof schema.properties === "object" &&
        !Array.isArray(schema.properties)
        ? schema.properties
        : null;
    const entries = Object.entries(payload).filter(([key, value]) => {
        if (value === undefined || value === null || value === "")
            return false;
        if (key === "additionalFields" ||
            key === "error" ||
            key === "errorCode" ||
            key === "outcome" ||
            key === "summary") {
            return false;
        }
        if (properties) {
            const prop = properties[key];
            if (!prop)
                return false;
            if (prop.showInReport === false)
                return false;
        }
        return true;
    });
    return entries.map(([key, value]) => {
        const prop = properties?.[key];
        const customLabel = typeof prop?.columnName === "string" && prop.columnName.trim()
            ? prop.columnName.trim()
            : typeof prop?.title === "string" && prop.title.trim()
                ? prop.title.trim()
                : null;
        return {
            key,
            label: customLabel || humanizeOutboundKey(key),
            value: formatOutboundValue(value),
        };
    });
}
export function humanizeOutboundKey(key) {
    return key
        .replace(/[_-]+/g, " ")
        .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
        .replace(/\s+/g, " ")
        .trim()
        .replace(/^./, (char) => char.toUpperCase());
}
export function normalizeScientificNotation(value) {
    if (value === null || value === undefined)
        return "";
    let str = String(value).trim();
    if (!str)
        return "";
    if (/[eE][+-]?\d+/.test(str)) {
        const num = Number(str);
        if (Number.isFinite(num)) {
            str = BigInt(Math.round(num)).toString();
        }
    }
    return str;
}
export function formatOutboundValue(value) {
    if (value === null || value === undefined)
        return "";
    if (value instanceof Date)
        return value.toLocaleString();
    if (typeof value === "boolean")
        return value ? "Yes" : "No";
    if (typeof value === "number") {
        if (!Number.isFinite(value))
            return "";
        return normalizeScientificNotation(value);
    }
    if (typeof value === "string") {
        return normalizeScientificNotation(value);
    }
    try {
        return JSON.stringify(value);
    }
    catch {
        return String(value);
    }
}
function firstPayloadString(payload, keys) {
    if (!payload) {
        return null;
    }
    for (const key of keys) {
        const raw = payload[key];
        if (typeof raw === "string" && raw.trim().length > 0) {
            return raw.trim();
        }
        if (typeof raw === "number" || typeof raw === "bigint") {
            return String(raw);
        }
    }
    return null;
}
function usableName(value) {
    const trimmed = value?.trim();
    return trimmed && trimmed.toLowerCase() !== "unknown" ? trimmed : "";
}
export function resolveCallParticipantDisplay(call, outboundContext) {
    const isOutbound = Boolean(outboundContext?.isOutbound ||
        call.direction === "outbound" ||
        call.isOutbound === true ||
        call.source === "outbound_sip" ||
        call.source === "outbound_playbook_task");
    const isHuman = call.callType === "human";
    const inboundCallerName = usableName(call.caller.name) || "Unknown";
    const inboundCallerPhone = normalizeScientificNotation(call.caller.phone);
    const agentName = usableName(call.operatorName) || usableName(call.agent) || "Agent";
    const agentPhone = normalizeScientificNotation(call.inboundNumber || call.operatorPhoneNumber);
    if (!isOutbound) {
        if (isHuman) {
            const operatorPhone = normalizeScientificNotation(call.operatorPhoneNumber) || agentPhone;
            return {
                isOutbound: false,
                isHuman: true,
                callType: "human",
                caller: {
                    name: inboundCallerName,
                    phone: inboundCallerPhone,
                },
                callee: {
                    name: "Human Operator",
                    phone: operatorPhone,
                },
            };
        }
        return {
            isOutbound: false,
            isHuman: false,
            callType: "ai",
            caller: {
                name: inboundCallerName,
                phone: inboundCallerPhone,
            },
            callee: {
                name: agentName,
                phone: agentPhone,
            },
        };
    }
    if (isHuman) {
        const destinationPhone = normalizeScientificNotation(call.destinationNumber || call.caller.phone || "");
        return {
            isOutbound: true,
            isHuman: true,
            callType: "human",
            caller: {
                name: usableName(call.operatorName) || "Human Operator",
                phone: normalizeScientificNotation(call.from) ||
                    inboundCallerPhone ||
                    normalizeScientificNotation(call.operatorPhoneNumber) ||
                    agentPhone,
            },
            callee: {
                name: usableName(call.caller.name) || "Customer",
                phone: destinationPhone,
            },
        };
    }
    const taskInput = outboundContext?.taskInput;
    const calleeName = firstPayloadString(taskInput, [
        "contactName",
        "customerName",
        "callerName",
        "name",
        "fullName",
    ]) ||
        usableName(call.caller.name) ||
        "Unknown";
    const rawCalleePhone = firstPayloadString(taskInput, [
        "phoneNumber",
        "toPhoneNumber",
        "customerPhone",
        "callerPhone",
        "phone",
    ]) ||
        call.destinationNumber ||
        call.caller.phone ||
        call.inboundNumber ||
        "";
    const calleePhone = normalizeScientificNotation(rawCalleePhone);
    return {
        isOutbound: true,
        caller: {
            name: agentName,
            phone: normalizeScientificNotation(call.from) ||
                inboundCallerPhone ||
                agentPhone,
        },
        callee: {
            name: calleeName,
            phone: calleePhone,
        },
    };
}
export function resolveOutboundCallContext(params) {
    const { call, playbooks, runs = [], tasks, attempts } = params;
    const attempt = attempts.find((entry) => entry.id === call.outboundTaskAttemptId) ||
        attempts.find((entry) => entry.providerCallSid === call.id) ||
        null;
    const task = tasks.find((entry) => entry.id === call.outboundTaskId) ||
        (attempt ? tasks.find((entry) => entry.id === attempt.taskId) : null) ||
        null;
    const run = runs.find((entry) => entry.id === call.outboundPlaybookRunId) ||
        (task ? runs.find((entry) => entry.id === task.playbookRunId) : null) ||
        (attempt
            ? runs.find((entry) => entry.id === attempt.playbookRunId)
            : null) ||
        null;
    const playbookId = call.outboundPlaybookId ||
        task?.playbookId ||
        attempt?.playbookId ||
        run?.playbookId ||
        null;
    const playbook = playbookId
        ? playbooks.find((entry) => entry.id === playbookId) || null
        : null;
    const playbookName = playbook?.name || run?.playbookSnapshot?.name || playbookId || null;
    const isOutbound = Boolean(hasOutboundCallMetadata(call) || attempt || task || run || playbook);
    return {
        isOutbound,
        playbook,
        run,
        task,
        attempt,
        playbookId,
        playbookName,
        playbookDescription: playbook?.description ||
            run?.playbookSnapshot?.conversationInstructions ||
            null,
        taskStatus: task?.status || attempt?.status || null,
        outcome: task?.latestOutcome || attempt?.outcome || null,
        attemptNumber: attempt?.attemptNumber ?? null,
        attemptCount: task?.attemptCount ?? null,
        maxAttempts: task?.maxAttempts ?? null,
        taskInput: task?.input || null,
        structuredResult: task?.result || attempt?.structuredResult || null,
        summary: attempt?.summary || null,
        runId: call.outboundPlaybookRunId ||
            task?.playbookRunId ||
            attempt?.playbookRunId ||
            null,
        taskId: call.outboundTaskId || task?.id || attempt?.taskId || null,
        attemptId: call.outboundTaskAttemptId || attempt?.id || null,
    };
}
